<template>
	<div v-if="selectedProduct" class="relative flex flex-col">
		<div class="absolute top-0 right-0">
			<Favorite
				wrapper-size="[30px]"
				rounded="none"
				:product-id="selectedProduct?.Id!"
				:product="selectedProduct"
				:variant-parametrics-number="
					parametrics && Object.keys(parametrics).length
						? Object.keys(parametrics).length
						: 0
				"
			/>
		</div>
		<div class="flex flex-col gap-6">
			<div class="flex flex-col md:gap-2 gap-1">
				<div class="pr-8">
					<h1
						data-testid="product-title"
						class="mb-1.5 lg:text-2xl text-xl font-bold"
					>
						{{ selectedProduct.VariantName || selectedProduct.Name }}
					</h1>
					<span
						class="text-gray-super-dark"
						v-html="selectedProduct.SubHeader"
					></span>

					<ProductSpecialPriceLabel
						v-if="priceListId"
						:price-list-id="priceListId"
						:special-price="specialPrice"
						class="!relative !p-0 !text-red-light !bg-transparent !max-w-full !justify-start"
					/>
					<ProductPromotionText
						:product="selectedProduct"
						:special-price="specialPrice"
						class="!relative !p-0 !text-red-light !bg-transparent !max-w-full !justify-start"
					/>
				</div>
				<p class="mb-2.5 text-sm text-gray-super-dark">
					{{ $t("item_number", { value: selectedProduct.PartNo }) }}
				</p>
				<LazyLoadWrapper>
					<div
						v-if="selectedProduct.SubDescription || selectedProduct.Description"
						class="mb-5 text-[15px] leading-[1.75]"
					>
						<div
							class="inline"
							v-html="
								selectedProduct.SubDescription || selectedProduct.Description
							"
						/>
					</div>
					<div
						class="md:py-5 py-2.5 w-full flex justify-between items-center border-b border-t border-gray-300 text-center"
					>
						<Price
							:old-price="oldPrice"
							size="big"
							:price="selectedProduct.PriceIncVat"
							:show-save-text="true"
							class="mx-auto"
						/>
					</div>
					<div v-if="historicalLowestPrice" class="text-sm text-left">
						<Popover class="relative">
							<PopoverButton
								id="history-popover-button"
								class="flex items-center gap-1 outline-none text-gray-super-dark"
							>
								<InformationCircleIcon class="w-5 h-5 min-w-5" />
								{{ $t("View price history") }}
							</PopoverButton>
							<PopoverPanel
								id="history-popover-panel"
								class="absolute top-6 left-0 z-10 py-1 px-2 min-w-56 max-h-[480px] bg-white border border-gray-600"
							>
								{{
									$t("Lowest price in the last 30 days: {value}", {
										value: formatPrice(historicalLowestPrice)
									})
								}}
							</PopoverPanel>
						</Popover>
					</div>
					<ProductProductAttributesVariantConfigurator
						:parametrics="parametrics"
					/>
					<div class="flex gap-3">
						<div class="mb-4 flex-1 flex flex-col">
							<div class="mb-4 px-4">
								<AddToCart
									:stock="productStock"
									:stock-status="stockStatus"
									hide-qty
								/>
							</div>

							<div class="flex flex-col justify-center">
								<ProductProductAttributesStockSituation
									:stock-status="stockStatus"
								/>
							</div>
						</div>
					</div>
					<DeliveryConditions :stock-status="stockStatus" class="md:mb-5" />
					<div
						class="py-2.5 px-[5px] flex items-center justify-center text-[13px] bg-gray-100"
					>
						{{ $t("product.attributes.customer_service") }}
						<nuxt-link
							:to="formattedCustomerServiceNumber"
							class="ml-1 underline"
						>
							{{ customerServiceNumber }}
						</nuxt-link>
					</div>
					<div class="py-2.5 px-[5px] flex justify-center items-center gap-2.5">
						<nuxt-img
							v-for="{ name, image, height } in paymentMethods"
							:key="name"
							:src="image"
							:alt="name"
							:style="{ height }"
						/>
					</div>
				</LazyLoadWrapper>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { GroupedParametrics } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import Price from "./Price.vue";
import DeliveryConditions from "./DeliveryConditions.vue";
import AddToCart from "./AddToCart/AddToCart.vue";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import type {
	ProductAttributes,
	SpecialPrice
} from "~/types/storyblok/storyblok";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";

interface Props {
	parametrics?: GroupedParametrics | undefined;
	priceListId?: number | null;
	specialPrice?: SpecialPrice;
	blok?: ProductAttributes;
}

const { parametrics, blok } = defineProps<Props>();

const { selectedProduct, oldPrice, productStock } = useProductAttributes();

const { lowestPrice: historicalLowestPrice } = storeToRefs(useProductStore());
const appStore = useAppStore();
const { formatPrice } = useFormatPrice({ groupThousands: true });

useExternalScript("//api.videoly.co/1/quchbox/0/847/quch.js");

const { getStockStatus } = useStockStatus();
const stockStatus = computed(() =>
	getStockStatus(selectedProduct.value?.FlagIdSeed, !!productStock.value)
);

const customerServiceNumber = computed<string>(() => {
	return appStore?.customerServicePhone
		? appStore.customerServicePhone
		: "031 18 40 00";
});

const formattedCustomerServiceNumber = computed(() => {
	return `tel:${customerServiceNumber.value.replaceAll(" ", "")}`;
});

const paymentMethods = computed(() => {
	if (blok?.payment_methods) {
		return blok.payment_methods.map(({ image, maxHeight }) => ({
			name: image.name || "",
			image: image?.filename || "",
			height: maxHeight ? `${maxHeight}px` : "auto"
		}));
	}
	return [];
});
</script>
